import { Component, ViewEncapsulation, Inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { entAnimations } from 'src/app/animations';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { ImageService } from 'src/app/services/profile-picture/images.service';
import { UsersService } from 'src/app/services/users/users.service';
import { IsmPhoto } from 'src/app/models/ism-photo.model';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FileDropEntry } from '../shared/file-drop/file-drop.entry';
import { FileSystemFileEntry } from '../shared/file-drop/dom.types';

export interface ImageUploadDialogModel {
  userOid: string;
  isSelf: boolean;
  selectedFileUrl: string;
}

@Component({
  selector: 'app-image-upload-dialog',
  templateUrl: './image-upload-dialog.component.html',
  styleUrl: './image-upload-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class ImageUploadDialogComponent {
  public files: FileDropEntry[] = [];
  isCropperOpen = false;
  imageChangedEvent: Event | null;
  base64String = '';
  selectedFileUrl: string | null = null;
  imageUrl: string;
  scale = 1;
  transform = {};
  croppedImage = '';
  validFileFormat = true;
  invalidFileType = '';
  validFileResolution = true;
  showTooltip = false;
  showPreview = false;
  showDropzone = false;
  agreeTermsOne = false;
  agreeTermsTwo = false;
  isConfirmingDelete = false;
  progressBarWidth = '0%';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ImageUploadDialogModel,
    private _dialogRef: MatDialogRef<ImageUploadDialogComponent>,
    private _imageService: ImageService,
    private _userService: UsersService
  ) {}

  public async dropped(files: FileDropEntry[]) {
    this.files = files;
    for (const droppedFile of files) {
      // Check if file
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file(async (file: File) => {
          if (this._imageService.isValidImageFile(file)) {
            this.validFileFormat = true;

            // Convert to base64 to store display in cropper
            const base64String = (
              await this._imageService.convertFileToBase64(file)
            ).toString();

            // Check image resolution
            const isValidResolution =
              await this._imageService.isValidImageResolution(
                base64String,
                5000,
                5000
              );
            if (isValidResolution) {
              this.base64String = base64String.toString();
              this.showDropzone = false;
              this.isCropperOpen = true;
            } else {
              this.validFileResolution = false;
            }
          } else {
            const fileExtension = file.name.split('.').pop();
            this.invalidFileType = fileExtension;
            this.validFileFormat = false;
          }
        });
      } else {
        // It was a directory (empty directories are added, otherwise only files)
        this.validFileFormat = false;
      }
    }
  }

  applyZoom(): void {
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  fileChangeEvent(event: Event): void {
    this.imageChangedEvent = event;
  }

  // Handle image cropping
  async imageCropped(event: ImageCroppedEvent) {
    this.imageUrl = event.objectUrl;
    this.croppedImage = await this._imageService.blobToBase64(event.blob);
  }

  zoomIn(): void {
    if (this.scale < 3) {
      this.scale += 0.1;
      this.applyZoom();
    }
  }

  zoomOut(): void {
    if (this.scale > 0.1) {
      this.scale -= 0.1;
      this.applyZoom();
    }
  }

  // React tho mouse wheel for slider
  handleMouseWheel(event: WheelEvent) {
    event.preventDefault();
    const delta = Math.sign(event.deltaY);
    const step = 0.1;
    if (delta > 0) {
      // Zoom out
      this.scale = Math.max(0.1, this.scale - step);
    } else if (delta < 0) {
      // Zoom in
      this.scale = Math.min(3, this.scale + step);
    }
    this.applyZoom();
  }

  // Save image - self
  saveImageSelf() {
    this._imageService.setImageUrl(this.imageUrl); // To update pictuer in sidenav
    const ismPhoto: IsmPhoto = {
      oid: this.data.userOid,
      data: this.croppedImage,
    };
    if (this.data.selectedFileUrl === null) {
      // If null we call save, else update
      this._imageService
        .addProfilePicture(ismPhoto)
        .subscribe(error => console.log(error));
      this._userService.updateMyUser(true);
    } else {
      this._imageService
        .updateProfilePicture(ismPhoto)
        .subscribe(error => console.log(error));
      this._userService.updateMyUser(true);
    }
    this.closeDialog(this.croppedImage);
    this.closeCropper();
  }

  // Save image - user
  saveImage() {
    const ismPhoto: IsmPhoto = {
      oid: this.data.userOid,
      data: this.croppedImage,
    };
    if (this.data.selectedFileUrl === null) {
      this._imageService
        .addProfilePicture(ismPhoto)
        .subscribe(error => console.log(error));
      this._userService.updateMyUsers(this.data.userOid, true);
    } else {
      this._imageService
        .updateProfilePicture(ismPhoto)
        .subscribe(error => console.log(error));
      this._userService.updateMyUsers(this.data.userOid, true);
    }
    this.closeDialog(this.croppedImage);
    this.closeCropper();
  }

  validate() {
    this.showPreview = true;
    this.showDropzone = false;
    this.isCropperOpen = false;
  }

  closeCropper(): void {
    this.isCropperOpen = false;
  }

  closeDialog(fileUrl: string | null) {
    this._dialogRef.close(fileUrl);
  }
}
